import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Zap, Moon, Smartphone, Briefcase, Coffee, Dumbbell, Heart, Book, Flame,
  Sun, Sparkles, PhoneOff, Bed, Utensils, Wine, Cigarette, Gamepad2, Tv,
  ShoppingCart, Clock, Target,
} from 'lucide-react';
import { nexaApi } from './api';

// Icon key (used by backend / admin extractor) → Lucide component + default color.
const ICONS = {
  zap: { C: Zap, color: '#FACC15' },
  moon: { C: Moon, color: '#818CF8' },
  smartphone: { C: Smartphone, color: '#F87171' },
  briefcase: { C: Briefcase, color: '#FB923C' },
  coffee: { C: Coffee, color: '#D6B08C' },
  dumbbell: { C: Dumbbell, color: '#38BDF8' },
  heart: { C: Heart, color: '#F87171' },
  book: { C: Book, color: '#A78BFA' },
  flame: { C: Flame, color: '#FB923C' },
  sun: { C: Sun, color: '#FACC15' },
  sparkles: { C: Sparkles, color: '#A78BFA' },
  'phone-off': { C: PhoneOff, color: '#F87171' },
  bed: { C: Bed, color: '#818CF8' },
  utensils: { C: Utensils, color: '#FB923C' },
  wine: { C: Wine, color: '#F87171' },
  cigarette: { C: Cigarette, color: '#F87171' },
  gamepad: { C: Gamepad2, color: '#A78BFA' },
  tv: { C: Tv, color: '#A78BFA' },
  'shopping-cart': { C: ShoppingCart, color: '#FB923C' },
  clock: { C: Clock, color: '#38BDF8' },
  target: { C: Target, color: '#F87171' },
};

const glowOf = (color) => {
  // Convert #RRGGBB to rgba with 0.25 alpha for the soft top glow.
  const n = parseInt(color.replace('#', ''), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, 0.25)`;
};

const israelHour = () =>
  parseInt(
    new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: false, timeZone: 'Asia/Jerusalem' }).format(new Date()),
    10,
  ) % 24;

const isActive = (r, h) => (r.from_hour < r.to_hour ? h >= r.from_hour && h < r.to_hour : h >= r.from_hour || h < r.to_hour);
const fmtWindow = (r) =>
  `${String(r.from_hour).padStart(2, '0')}:00–${String(r.to_hour).padStart(2, '0')}:00`;

export const ActiveSigns = () => {
  const [hour, setHour] = useState(israelHour());
  const [rules, setRules] = useState([]);

  const load = () => {
    nexaApi.get('/rules').then(({ data }) => setRules(data.rules)).catch(() => {});
  };

  useEffect(() => {
    load();
    const t = setInterval(() => setHour(israelHour()), 60000);
    // Reload rules on focus so chat-added rules show up on next glance.
    const onFocus = () => {
      setHour(israelHour());
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const active = rules.filter((r) => isActive(r, hour));
  if (active.length === 0) return null;

  return (
    <div className="mb-6" data-testid="nexa-active-signs">
      <div className="flex gap-3 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {active.map((r, i) => {
          const spec = ICONS[r.icon] || ICONS.sparkles;
          const color = r.color || spec.color;
          const IconComp = spec.C;
          const isBan = r.mode === 'ban';
          return (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08, type: 'spring', stiffness: 300, damping: 24 }}
              data-testid={`nexa-sign-${r.id}`}
              className="relative shrink-0 w-[104px] rounded-2xl border border-white/10 bg-[#0A0A0A] px-3 pt-3 pb-2.5 flex flex-col items-center text-center overflow-hidden"
            >
              <span
                className="absolute -top-6 w-20 h-20 rounded-full blur-2xl pointer-events-none"
                style={{ backgroundColor: glowOf(color) }}
              />
              <motion.div
                className="relative w-11 h-11 flex items-center justify-center"
                animate={{ opacity: [1, 0.7, 1] }}
                transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
              >
                <IconComp className="w-7 h-7" strokeWidth={1.5} style={{ color }} />
                {isBan && (
                  <svg
                    viewBox="0 0 44 44"
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    aria-hidden="true"
                  >
                    <line
                      x1="8" y1="36" x2="36" y2="8"
                      stroke={color} strokeWidth="2.2" strokeLinecap="round" opacity="0.85"
                    />
                  </svg>
                )}
              </motion.div>
              <span className="text-[11px] font-semibold text-white/90 leading-tight mt-1.5">{r.label}</span>
              <span className="text-[9px] text-white/35 tracking-wider mt-0.5">{fmtWindow(r)}</span>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};
